import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { syncNativeStatusBarTheme } from "@/lib/native";

export type ThemePreference = "light" | "dark" | "system";
export type DensityPreference = "comfortable" | "compact";
export type ResolvedTheme = "light" | "dark";

const STORAGE_KEY = "twido-v2-theme";
const DENSITY_STORAGE_KEY = "twido-v2-density";

interface ThemeContextValue {
  /** Resolved active theme (light or dark). */
  theme: ResolvedTheme;
  preference: ThemePreference;
  setPreference: (preference: ThemePreference) => void;
  toggleTheme: () => void;
  density: DensityPreference;
  setDensity: (density: DensityPreference) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readStoredPreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark" || stored === "system") {
      return stored;
    }
  } catch {
    /* private browsing */
  }
  return "system";
}

function resolveTheme(preference: ThemePreference): ResolvedTheme {
  if (preference === "light" || preference === "dark") return preference;
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

function applyTheme(theme: ResolvedTheme) {
  const root = document.documentElement;
  root.classList.toggle("dark", theme === "dark");
  root.style.colorScheme = theme;
}

function readStoredDensity(): DensityPreference {
  try {
    const stored = localStorage.getItem(DENSITY_STORAGE_KEY);
    if (stored === "comfortable" || stored === "compact") return stored;
  } catch {
    /* ignore */
  }
  return "comfortable";
}

function applyDensity(density: DensityPreference) {
  document.documentElement.dataset.density = density;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] =
    useState<ThemePreference>(readStoredPreference);
  const [density, setDensityState] = useState<DensityPreference>(readStoredDensity);
  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(() =>
    resolveTheme("system"),
  );

  const theme = useMemo(
    () => (preference === "system" ? systemTheme : preference),
    [preference, systemTheme],
  );

  // Apply before descendants render so hooks that read `--chart-*` from
  // getComputedStyle(document.documentElement) see the active theme. A
  // useEffect here runs after child effects and leaves charts on stale colors.
  applyTheme(theme);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, preference);
    } catch {
      /* ignore */
    }
  }, [preference]);

  // Keep the native status-bar text color in sync with the resolved theme.
  // No-op on web.
  useEffect(() => {
    void syncNativeStatusBarTheme(theme);
  }, [theme]);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => setSystemTheme(mq.matches ? "dark" : "light");
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
  }, []);

  const toggleTheme = useCallback(() => {
    setPreferenceState((prev) => {
      const current = prev === "system" ? resolveTheme("system") : prev;
      return current === "dark" ? "light" : "dark";
    });
  }, []);

  const setDensity = useCallback((next: DensityPreference) => {
    applyDensity(next);
    setDensityState(next);
    try {
      localStorage.setItem(DENSITY_STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  }, []);

  return (
    <ThemeContext.Provider
      value={{
        theme,
        preference,
        setPreference,
        toggleTheme,
        density,
        setDensity,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return ctx;
}

/** Apply theme before React hydrates — called from index.html inline script. */
export function initThemeFromStorage() {
  applyTheme(resolveTheme(readStoredPreference()));
}

export function initDensityFromStorage() {
  applyDensity(readStoredDensity());
}
